import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { fetchOverviewPage, isAbort } from './api';
import { filterKey, useBlogFilter } from './urlFilterStore';
import { BlogOverviewPost, BlogOverviewResult, FeedBlogOverviewProps, FeedInitialPage } from './types';

/**
 * One category's feed (ported from regulator-marine's feedBlogOverview block): the newest post as a
 * featured card beside the category's summary, then a grid of cards with Load More.
 *
 * Hides itself while the filter bar has a different category selected. Author and type selections
 * narrow this feed's own query.
 */

type FeedState =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'ready'; featured: BlogOverviewPost | null; items: BlogOverviewPost[]; page: number; hasMore: boolean }
    | { status: 'loadingMore'; featured: BlogOverviewPost | null; items: BlogOverviewPost[]; page: number }
    | { status: 'errorMore'; featured: BlogOverviewPost | null; items: BlogOverviewPost[]; page: number; message: string };

type FeedAction =
    | { type: 'reset' }
    | { type: 'firstPage'; result: BlogOverviewResult }
    | { type: 'moreStarted' }
    | { type: 'morePage'; result: BlogOverviewResult }
    | { type: 'failed'; message: string };

const LOAD_ERROR = 'These posts could not be loaded.';

function reducer(state: FeedState, action: FeedAction): FeedState {
    switch (action.type) {
        case 'reset':
            return { status: 'loading' };
        case 'firstPage':
            return { status: 'ready', featured: action.result.featured, items: action.result.items, page: 1, hasMore: action.result.hasMore };
        case 'moreStarted':
            if (state.status !== 'ready' && state.status !== 'errorMore') {
                return state;
            }
            return { status: 'loadingMore', featured: state.featured, items: state.items, page: state.page };
        case 'morePage':
            if (state.status !== 'loadingMore') {
                return state;
            }
            return {
                status: 'ready',
                featured: state.featured,
                items: state.items.concat(action.result.items),
                page: state.page + 1,
                hasMore: action.result.hasMore,
            };
        case 'failed':
            if (state.status === 'loadingMore') {
                return { status: 'errorMore', featured: state.featured, items: state.items, page: state.page, message: action.message };
            }
            return { status: 'error', message: action.message };
    }
}

// The server renders page 1 into the HTML (FeedBlogOverview.cshtml). When it was rendered for the filter
// the URL holds now, the feed starts from it: same markup, no request, and nothing on the page moves.
function initialState(initial: FeedInitialPage | null, currentKey: string): FeedState {
    if (initial && initial.filterKey === currentKey) {
        return reducer({ status: 'loading' }, { type: 'firstPage', result: initial.result });
    }
    return { status: 'loading' };
}

export default function FeedBlogOverview(props: FeedBlogOverviewProps) {
    const { rootId, categoryKey, categoryName, categorySummary, allPostLoadedMessage, pageSize, initial, categories } = props;
    const filter = useBlogFilter();
    const [state, dispatch] = useReducer(reducer, null, () => initialState(initial, filterKey(filter)));
    const inFlight = useRef<AbortController | null>(null);
    // True while the server-rendered page 1 is still the current one, so the first effect run skips its fetch.
    const usingServerPage = useRef(state.status === 'ready');

    // An all-posts feed (no category of its own) is always shown and follows the filter bar's category.
    // A single-category feed hides while the filter bar has a different category selected.
    const allPosts = categoryKey === '';
    const queryCategory = allPosts ? filter.category : categoryKey;
    const visible = allPosts || filter.category === '' || filter.category === categoryKey;
    const selected = allPosts ? (categories ?? []).find(c => c.key === filter.category) : undefined;
    const heading = selected ? selected.name : categoryName;
    const summary = allPosts ? (selected ? selected.summary : categorySummary) : categorySummary;
    // What the empty messages call these posts: a category name, or nothing for every post.
    const subject = allPosts ? (selected ? selected.name : '') : categoryName;
    // Joined so the effect below reruns on a change of value, not on each new array.
    const authorsKey = filter.authors.join(',');
    const typesKey = filter.types.join(',');

    const load = useCallback((page: number) => {
        inFlight.current?.abort();
        const controller = new AbortController();
        inFlight.current = controller;
        dispatch(page === 1 ? { type: 'reset' } : { type: 'moreStarted' });

        const request = {
            rootId,
            categoryKey: queryCategory,
            authors: authorsKey ? authorsKey.split(',') : [],
            types: typesKey ? typesKey.split(',') : [],
            page,
            pageSize,
        };
        fetchOverviewPage(request, controller.signal)
            .then(result => dispatch(page === 1 ? { type: 'firstPage', result } : { type: 'morePage', result }))
            .catch(error => {
                if (isAbort(error)) {
                    return;
                }
                console.error(error);
                dispatch({ type: 'failed', message: LOAD_ERROR });
            });
    }, [rootId, queryCategory, authorsKey, typesKey, pageSize]);

    // A hidden feed does not fetch; it loads fresh when its category is shown again.
    useEffect(() => {
        if (!visible) {
            usingServerPage.current = false;
            return undefined;
        }
        if (usingServerPage.current) {
            usingServerPage.current = false;
            return undefined;
        }
        load(1);
        return () => inFlight.current?.abort();
    }, [visible, load]);

    if (!visible) {
        return null;
    }

    const narrowed = filter.authors.length > 0 || filter.types.length > 0;

    // A category with no published posts has nothing to say on the unfiltered overview, so it renders
    // nothing there (as regulator's did). The empty message is kept for when the visitor asked for this
    // category, or narrowed by author or type, since then silence would look like a broken page. The
    // all-posts feed is the page's main content, so it always explains an empty result.
    const asked = allPosts || filter.category === categoryKey || narrowed;
    if (state.status === 'ready' && !state.featured && !asked) {
        return null;
    }

    return (
        <section className="blog-overview__feed" aria-label={`${heading} posts`} aria-busy={state.status === 'loading' || state.status === 'loadingMore'}>
            <FeedBody state={state} heading={heading} summary={summary} subject={subject} narrowed={narrowed} onRetry={() => load(1)} />
            <FeedFooter state={state} allPostLoadedMessage={allPostLoadedMessage} onMore={() => state.status !== 'loading' && state.status !== 'error' && load(state.page + 1)} />
        </section>
    );
}

type FeedBodyProps = {
    state: FeedState;
    heading: string;
    summary: string | null;
    subject: string;
    narrowed: boolean;
    onRetry: () => void;
};

// "Men's Recovery posts" for a category, plain "posts" for every post.
function postsLabel(subject: string): string {
    return subject ? `${subject} posts` : 'posts';
}

function FeedBody({ state, heading, summary, subject, narrowed, onRetry }: FeedBodyProps) {
    if (state.status === 'loading') {
        return <div className="blog-overview__placeholder" role="status">Loading {postsLabel(subject)}…</div>;
    }
    if (state.status === 'error') {
        return (
            <div className="blog-overview__message" role="alert">
                <p>{state.message}</p>
                <button type="button" className="blog-overview__button" onClick={onRetry}>Try again</button>
            </div>
        );
    }
    if (!state.featured) {
        return (
            <div className="blog-overview__message" role="status">
                <p>{narrowed ? `No ${postsLabel(subject)} match the selected filters.` : `There are no ${postsLabel(subject)} yet.`}</p>
            </div>
        );
    }
    return (
        <>
            <FeaturedPost post={state.featured} heading={heading} summary={summary} />
            {state.items.length > 0 && (
                <ul className="blog-overview__grid">
                    {state.items.map(post => (
                        <li key={post.id} className="blog-overview__grid-item">
                            <PostCard post={post} />
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}

type FeaturedPostProps = {
    post: BlogOverviewPost;
    heading: string;
    summary: string | null;
};

function FeaturedPost({ post, heading, summary }: FeaturedPostProps) {
    return (
        <div className="blog-overview__headline">
            <a className="blog-overview__featured" href={post.url}>
                {/* width/height are the featuredHeadline crop, for the aspect ratio; CSS sets the displayed size. */}
                {post.featuredImage && <img className="blog-overview__featured-image" src={post.featuredImage} alt="" loading="lazy" width={1024} height={719} />}
                <span className="blog-overview__featured-overlay" aria-hidden="true"></span>
                <span className="blog-overview__featured-content">
                    {post.categoryName && <span className="blog-overview__eyebrow">{post.categoryName}</span>}
                    <span className="blog-overview__featured-title">{post.name}</span>
                    {post.authorName && <span className="blog-overview__eyebrow">{post.authorName}</span>}
                    {post.summary && <span className="blog-overview__featured-summary">{post.summary}</span>}
                    <span className="blog-overview__read-more">Read more</span>
                </span>
            </a>
            <div className="blog-overview__intro">
                <h2 className="blog-overview__category">{heading}</h2>
                {/* Editor-authored rich text from the category node, rendered the same way the RTE widgets are. */}
                {summary && <div className="blog-overview__intro-text rte" dangerouslySetInnerHTML={{ __html: summary }} />}
            </div>
        </div>
    );
}

function PostCard({ post }: { post: BlogOverviewPost }) {
    return (
        <a className="blog-overview__card" href={post.url}>
            <span className="blog-overview__card-image-wrap">
                {/* The blogCard crop's size; CSS sets the displayed size. */}
                {post.cardImage && <img className="blog-overview__card-image" src={post.cardImage} alt="" loading="lazy" width={568} height={398} />}
            </span>
            <span className="blog-overview__card-title">{post.name}</span>
            {post.summary && <span className="blog-overview__card-summary">{post.summary}</span>}
            <span className="blog-overview__card-arrow fa fa-long-arrow-right" aria-hidden="true"></span>
        </a>
    );
}

type FeedFooterProps = {
    state: FeedState;
    allPostLoadedMessage: string | null;
    onMore: () => void;
};

function FeedFooter({ state, allPostLoadedMessage, onMore }: FeedFooterProps) {
    if (state.status === 'loading' || state.status === 'error') {
        return null;
    }
    if (state.status === 'ready' && !state.featured) {
        return null;
    }
    if (state.status === 'ready' && !state.hasMore) {
        return allPostLoadedMessage
            ? <div className="blog-overview__footer rte" dangerouslySetInnerHTML={{ __html: allPostLoadedMessage }} />
            : null;
    }
    return (
        <div className="blog-overview__footer">
            {state.status === 'errorMore' && <p className="blog-overview__footer-error" role="alert">{state.message}</p>}
            <button type="button" className="blog-overview__button" onClick={onMore} disabled={state.status === 'loadingMore'}>
                {state.status === 'loadingMore' ? 'Loading…' : state.status === 'errorMore' ? 'Try again' : 'Load More'}
            </button>
        </div>
    );
}
